import React, { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Text, Checkbox, CardHeader } from '@amzn/storm-ui';
import { Form } from '@amzn/storm-ui-column-form-kit';
import { MessageBundle } from '@amzn/arb-tools';
import InputController from '../../../shared/components/form/InputController';
import DropdownController from '../../../shared/components/form/DropdownController';
import ReadOnlyFormField from '../../../shared/components/form/ReadOnlyFormRow';
import { RegistrationFormCard } from '../../../shared/features/account-registration/style';
import { RequestPageContext } from '../../../shared/services/activation/activation';
import {
  MANAGER_ACCOUNT_LABEL_KEY,
  TIME_ZONE_LABEL_KEY,
  TIME_ZONE_TOOLTIP_KEY,
  INDUSTRY_LABEL_KEY,
  PARTNER_ID_LABEL_KEY,
  OPTIONAL_LABEL_KEY,
  INVOICE_COUNTRY_LABEL_KEY,
  INVOICE_COUNTRY_TOOLTIP_KEY,
  PO_NUMBER_LABEL_KEY,
  PO_NUMBER_TOOLTIP_KEY,
  PO_CHECKBOX_LABEL_KEY,
  SELECT_PLACEHOLDER_KEY,
  REQUIRED_ERROR_KEY,
  SETTINGS_HEADER_KEY,
} from '../constants';

interface SettingsProps {
  intl: MessageBundle;
  context: RequestPageContext;
}

const Settings: FC<SettingsProps> = ({ intl, context }) => {
  const { watch, setValue } = useFormContext();
  const select = intl.getMessage(SELECT_PLACEHOLDER_KEY);
  const required = intl.getMessage(REQUIRED_ERROR_KEY);

  const timezoneItems = useMemo(
    () =>
      (context.timeZoneOptions || []).map((tz) => ({ value: tz, label: tz })),
    [context.timeZoneOptions],
  );

  const industryItems = useMemo(
    () =>
      (context.industryOptions || []).map((ind) => ({
        value: ind,
        label: ind,
      })),
    [context.industryOptions],
  );

  const marketplaceItems = useMemo(
    () =>
      (context.marketplaceCountries || []).map((c) => ({
        value: c.country,
        label: c.countryName,
      })),
    [context.marketplaceCountries],
  );

  return (
    <RegistrationFormCard>
      <CardHeader padding="small">
        <Text data-testid="kyc-settings-title" size="xlarge">
          {intl.getMessage(SETTINGS_HEADER_KEY)}
        </Text>
      </CardHeader>
      <Form id="kyc-settings">
        <ReadOnlyFormField
          label={intl.getMessage(MANAGER_ACCOUNT_LABEL_KEY)}
          id="managerAccountName"
          value={context.managerAccountName}
        />
        <DropdownController
          label={intl.getMessage(TIME_ZONE_LABEL_KEY)}
          overrideDropdownLabel={select}
          id="timezone-row"
          controllerName="timezone"
          items={timezoneItems}
          helpTipText={intl.getMessage(TIME_ZONE_TOOLTIP_KEY)}
          registerOptions={{ required }}
        />
        <DropdownController
          label={intl.getMessage(INDUSTRY_LABEL_KEY)}
          overrideDropdownLabel={select}
          id="industry-row"
          controllerName="industry"
          items={industryItems}
        />
        <InputController
          label={intl.getMessage(PARTNER_ID_LABEL_KEY)}
          id="partner-id-input"
          controllerName="partnerId"
          optionalTag={intl.getMessage(OPTIONAL_LABEL_KEY)}
        />
        <DropdownController
          label={intl.getMessage(INVOICE_COUNTRY_LABEL_KEY)}
          overrideDropdownLabel={select}
          id="marketplace-input"
          controllerName="marketplace"
          items={marketplaceItems}
          description={
            <Text color="secondary" size="xsmall">
              {intl.getMessage(INVOICE_COUNTRY_TOOLTIP_KEY)}
            </Text>
          }
          registerOptions={{ required }}
        />
        <ReadOnlyFormField
          label={intl.getMessage(PO_NUMBER_LABEL_KEY)}
          id="require-po-number"
          helpTipText={intl.getMessage(PO_NUMBER_TOOLTIP_KEY)}
        >
          <Checkbox
            label={intl.getMessage(PO_CHECKBOX_LABEL_KEY)}
            checked={watch('requirePoNumber')}
            onChange={(e) =>
              setValue(
                'requirePoNumber',
                (e.target as HTMLInputElement).checked,
              )
            }
          />
        </ReadOnlyFormField>
      </Form>
    </RegistrationFormCard>
  );
};

export default Settings;
